import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { serviceJsonLd } from "@/lib/service-jsonld";
import { faqPageJsonLd, type FaqItem } from "@/lib/faq-jsonld";
import { FaqAccordion } from "@/components/site/FaqAccordion";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { QuoteRequestForm } from "@/components/site/QuoteRequestForm";
import {
  CheckCircle2,
  ShieldCheck,
  Leaf,
  Award,
  MapPin,
  ArrowRight,
  Phone,
} from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/offerte/groepenkasten")({
  head: () => {
    const url = `${SITE_URL}/offerte/groepenkasten`;
    const title = "Groepenkast vervangen — offerte binnen 24 uur | Elvion Group Nederland";
    const description =
      "Groepenkast vervangen door erkende installateurs? Vraag een offerte op maat aan: 2 jaar garantie, NEN 1010, gratis advies en reactie binnen 24 uur.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbJsonLd([
          { name: "Offerte", path: "/offerte" },
          { name: "Groepenkasten", path: "/offerte/groepenkasten" },
        ]),
        serviceJsonLd({
          path: "/offerte/groepenkasten",
          name: "Groepenkast vervangen — offerte op maat",
          description:
            "Vraag een offerte aan voor het vervangen van uw groepenkast door erkende installateurs. NEN 1010, AEG-materialen met 15 jaar garantie, vaste prijs vooraf en reactie binnen 24 uur.",
          serviceType: "Groepenkast vervangen",
          offerCatalog: [
            "Groepenkast vervangen 1-fase",
            "Groepenkast vervangen 3-fase",
            "Uitbreiding groepenkast",
            "Aardlekautomaten plaatsen",
            "1-fase → 3-fase verzwaring",
            "Spoedservice groepenkast",
          ],
        }),
        faqPageJsonLd(FAQ_ITEMS, `${SITE_URL}/offerte/groepenkasten#faq`),
      ],
    };
  },
  component: GroepenkastOffertePage,
});

const FAQ_ITEMS: FaqItem[] = [
  {
    q: "Wat kost het vervangen van een groepenkast?",
    a: "De prijs hangt af van het aantal groepen, het type aardlekautomaten (ALS) en of de meterkast bereikbaar is. Via onze configurator ontvangt u binnen 24 uur een offerte op maat.",
  },
  {
    q: "Voldoet mijn nieuwe groepenkast aan NEN 1010?",
    a: "Ja. Al onze groepenkasten worden geplaatst volgens de meest actuele NEN 1010-norm, inclusief aardlekautomaten en correcte selectiviteit.",
  },
  {
    q: "Hoe lang duurt het vervangen van een groepenkast?",
    a: "Een standaard vervanging duurt gemiddeld 4 tot 6 uur. Onze monteur zorgt dat u dezelfde dag weer stroom heeft in de hele woning.",
  },
  {
    q: "Moet ik de stroom lang missen tijdens de werkzaamheden?",
    a: "De stroom is alleen uit tijdens het daadwerkelijk omklemmen — meestal een aantal uur. We plannen dit vooraf duidelijk met u in.",
  },
  {
    q: "Kan ik meteen uitbreiden van 1-fase naar 3-fase?",
    a: "Ja. Als u nu 1-fase heeft en overweegt om 3-fase aan te leggen (bijvoorbeeld voor een laadpaal of inductie), kunnen wij dit meteen meenemen in de offerte.",
  },
  {
    q: "Krijg ik garantie op de nieuwe groepenkast?",
    a: "U krijgt 2 jaar garantie op materiaal én installatie. Bovendien werken wij uitsluitend met erkende InstallQ-monteurs.",
  },
  {
    q: "In welke plaatsen kan ik een groepenkast laten vervangen?",
    a: "Wij werken landelijk — o.a. in Amsterdam, Rotterdam, Den Haag, Utrecht, Eindhoven, Almere, Tilburg, Nijmegen en Arnhem. Zie onze dekkingspagina voor alle plaatsen.",
  },
];

const TRUST_STRIP = [
  { icon: ShieldCheck, label: "2 jaar garantie" },
  { icon: Leaf, label: "Duurzaam" },
  { icon: Award, label: "Gecertificeerd (NEN1010)" },
  { icon: MapPin, label: "Landelijk beschikbaar" },
];

const USPS = [
  "2 jaar garantie op onze installatie",
  "Erkende installateurs (NEN1010 & InstallQ)",
  "Gratis vrijblijvend advies",
  "Offerte binnen 24 uur",
];

function GroepenkastOffertePage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Trust strip */}
        <div className="border-b border-border bg-foreground text-background">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-3 text-sm sm:px-6 lg:px-8">
            <a href="tel:0850806695" className="flex items-center gap-2 font-semibold hover:text-orange">
              <Phone className="h-4 w-4" />
              085 080 66 95
            </a>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {TRUST_STRIP.map((t) => (
                <div key={t.label} className="flex items-center gap-1.5">
                  <t.icon className="h-4 w-4 text-orange" />
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hero with form */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[oklch(0.25_0.04_240)] via-[oklch(0.22_0.05_250)] to-[oklch(0.18_0.04_260)] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,oklch(0.45_0.18_240/0.4),transparent_60%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_minmax(380px,440px)] lg:gap-16 lg:px-8 lg:py-20">
            <div>

              <h1 className="text-4xl font-extrabold leading-tight tracking-tight md:text-5xl lg:text-6xl">
                Meterkast vervangen,
                <br />
                voorkom overbelasting
              </h1>
              <p className="mt-5 max-w-xl text-base text-white/80 md:text-lg">
                Oude groepenkast of meterkast aan vervanging toe? Onze erkende
                installateurs plaatsen een veilige, toekomstbestendige
                groepenkast — geschikt voor laadpaal, warmtepomp en zonnepanelen.
              </p>

              <ul className="mt-8 space-y-3 text-base md:text-lg">
                {USPS.map((u) => (
                  <li key={u} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                    <span>{u}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <Link
                  to="/groepenkast-configurator"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white underline-offset-4 hover:underline"
                >
                  Of stel je eigen AEG groepenkast samen in de configurator
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:pt-2">
              <QuoteRequestForm
                service="groepenkast"
                title="AEG offerte op maat binnen 24 uur"
                subtitle="Vul je gegevens in. We bellen je terug voor een gratis advies."
                prefillSessionKey="groepenkast-configuratie"
              />
            </div>
          </div>
        </section>

        {/* Info section */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-3">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Wanneer een nieuwe groepenkast?</h2>
              <p className="mt-3 text-muted-foreground">
                Heb je nog draadzekeringen, slechts 1 of 2 groepen, of valt
                regelmatig de stroom uit? Dan is een vervanging vaak verstandig.
                Ook bij uitbreiding met een laadpaal, warmtepomp of zonnepanelen
                voldoet een oude kast meestal niet meer.
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Wat krijg je bij ons?</h2>
              <ul className="mt-3 space-y-2 text-foreground">
                {[
                  "Inmeetbezoek en advies op locatie",
                  "Nieuwe kast inclusief aardlekautomaten",
                  "Optioneel type B ALB voor laadpaal",
                  "Oplevering met NEN1010-meetrapport",
                  "2 jaar garantie op onze installatie",
                ].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Wat kost het?</h2>
              <p className="mt-3 text-muted-foreground">
                Elke groepenkast is maatwerk. De kosten hangen af van het aantal
                groepen, de fase (1 of 3), het type aardlekautomaat en of er
                extra voorzieningen nodig zijn zoals een overspanningsbeveiliging
                of laadpaalgroep. Vraag een vrijblijvende offerte aan voor een
                definitieve prijs.
              </p>
              <Link
                to="/groepenkast-configurator"
                className="mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted"
              >
                Open de configurator
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Stedenlinks */}
        <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 md:pb-20 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
            <h2 className="text-2xl font-bold text-foreground">Groepenkast vervangen in uw stad</h2>
            <p className="mt-2 text-muted-foreground">
              Onze erkende installateurs plaatsen groepenkasten door heel Nederland. Bekijk onze lokale pagina's voor meer informatie en prijzen.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {[
                { label: "Rotterdam", slug: "rotterdam" },
                { label: "Utrecht", slug: "utrecht" },
                { label: "Tilburg", slug: "tilburg" },
                { label: "Nijmegen", slug: "nijmegen" },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to="/groepenkast-vervangen/$stad"
                  params={{ stad: city.slug }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
                >
                  {city.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FaqAccordion
            id="groepenkast-faq"
            title="Veelgestelde vragen over groepenkast vervangen"
            intro="Alles over kosten, NEN 1010, doorlooptijd, garantie en 3-fase uitbreiding."
            items={FAQ_ITEMS}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
